import { useState, useMemo, useEffect } from 'react'
import type { JSX } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom'

import {
  Button,
  Link,
  PlusIcon,
  toast,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { TagEditDialog } from '~publish/components/TagEditDialog/TagEditDialog'
import type { TagFormValue } from '~publish/components/TagEditDialog/TagEditDialog'
import {
  useHasOrganizationAction,
  useIsFreeUser,
} from '~publish/contexts/AccountContext'
import { useTags } from '~publish/hooks/useTags'
import type { Tag } from '~publish/hooks/useTags'
import { actions as modalActions } from '~publish/legacy/modals/reducer'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'

import { TagsEmptyState } from './components/TagsEmptyState'
import { TagsErrorState } from './components/TagsErrorState'
import { TagsList } from './components/TagsList'
import { TagsLoadingSkeleton } from './components/TagsLoadingSkeleton'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'

const TagsSubtitle = (): JSX.Element => (
  <>
    Tags are visible to everyone in your organization.{' '}
    <Link
      href={`https://support.buffer.com/articles/creating-and-managing-tags-2XU5Rft4ri?${HC_UTM_PARAMS}`}
      external
    >
      Learn more
    </Link>
  </>
)

export const Tags = (): JSX.Element => {
  const isMobile = useIsMobileScreenSize()
  // Tag mutations are gated on the organization `edit` action server-side.
  const canManageTags = useHasOrganizationAction('manageTags')
  const isFreeUser = useIsFreeUser()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ tagId?: string }>()

  const [searchFilter, setSearchFilter] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)

  const {
    tags,
    loading: loadingTags,
    error: errorLoadingTags,
    createTag,
    updateTag,
    hasCreatePermission,
  } = useTags()

  const filteredTags = useMemo(() => {
    if (!searchFilter.trim()) return tags
    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchFilter.toLowerCase()),
    )
  }, [tags, searchFilter])

  // Handle URL-based dialog opening
  useEffect(() => {
    const currentPath = location.pathname

    if (currentPath.endsWith('/new')) {
      setCreateDialogOpen(true)
    } else if (params.tagId && currentPath.includes('/edit')) {
      setEditDialogOpen(true)
    }
  }, [location.pathname, params.tagId])

  const isEditPath = useMemo(
    () => location.pathname.includes('/edit'),
    [location.pathname],
  )

  useEffect(() => {
    if (params.tagId && isEditPath && tags.length > 0) {
      const tag = tags.find((t) => t.id === params.tagId)
      if (tag) {
        setEditingTag(tag)
      }
    }
  }, [params.tagId, tags, isEditPath])

  // Dialog handlers
  const handleCreateDialogClose = (open: boolean): void => {
    setCreateDialogOpen(open)
    if (!open) {
      navigate('/settings/tags', { replace: true })
    }
  }

  const handleEditDialogClose = (open: boolean): void => {
    setEditDialogOpen(open)
    if (!open) {
      setEditingTag(null)
      navigate('/settings/tags', { replace: true })
    }
  }

  const handleCreateTagClick = (): void => {
    if (!hasCreatePermission) {
      if (isFreeUser) {
        dispatch(modalActions.showTagsLimitUpgradeModal())
      } else {
        toast.error(
          'You have reached your tag limit of 250, please consider removing some tags to add new ones.',
        )
      }
    } else {
      navigate('/settings/tags/new')
    }
  }

  const handleEditTagClick = (tag: Tag): void => {
    navigate(`/settings/tags/${tag.id}/edit`)
  }

  const handleCreateTagSubmit = async (tag: TagFormValue): Promise<void> => {
    try {
      await createTag({ name: tag.name, color: tag.color })
    } catch {
      toast.error('Failed to create tag. Please try again later.')
    }
  }

  const handleEditTagSubmit = async (tag: TagFormValue): Promise<void> => {
    if (!tag.id) return

    try {
      await updateTag({ id: tag.id, name: tag.name, color: tag.color })
    } catch {
      toast.error('Failed to update tag. Please try again later.')
    }
  }

  // Action button for creating new tags
  const newTagButton = canManageTags && (
    <Button onClick={handleCreateTagClick}>
      {!isMobile && <PlusIcon />}
      {'New Tag'}
    </Button>
  )

  // View state logic
  const showEmptyState = !loadingTags && !errorLoadingTags && tags.length === 0
  const showTagsList = !loadingTags && !errorLoadingTags && tags.length > 0

  return (
    <>
      <NewSettingsPageLayout
        title="Tags"
        subtitle={<TagsSubtitle />}
        actions={showTagsList ? newTagButton : null}
        inlineActionsOnMobile
      >
        {loadingTags && <TagsLoadingSkeleton />}
        {errorLoadingTags && <TagsErrorState />}
        {showEmptyState && (
          <TagsEmptyState
            canManageTags={canManageTags}
            hasCreatePermission={hasCreatePermission}
            onCreateClick={handleCreateTagClick}
          />
        )}
        {showTagsList && (
          <TagsList
            searchFilter={searchFilter}
            onSearchChange={setSearchFilter}
            filteredTags={filteredTags}
            canManageTags={canManageTags}
            onEditTag={handleEditTagClick}
          />
        )}
      </NewSettingsPageLayout>

      {/* Create Tag Dialog */}
      <TagEditDialog
        open={createDialogOpen}
        onOpenChange={handleCreateDialogClose}
        onSubmit={handleCreateTagSubmit}
      />

      {/* Edit Tag Dialog */}
      {editingTag && (
        <TagEditDialog
          open={editDialogOpen}
          onOpenChange={handleEditDialogClose}
          onSubmit={handleEditTagSubmit}
          tag={editingTag}
        />
      )}

      <Outlet />
    </>
  )
}

export default Tags
